"use client";

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type React from "react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { cn } from "../../lib/utils";
import { Separator } from "../ui/separator";
import { Tabs as ShadcnTabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
  type BaseTabItem,
  filterVisibleTabs,
  renderTabTriggerContent,
} from "./tabs-shared";

// TabOption interface (extends BaseTabItem, no content field)
export interface TabOption extends BaseTabItem {
  ariaLabel?: string;
  badge?: string | number;
  disabled?: boolean;
  icon?: React.ReactNode;
  id: string | number;
  label: string;
  visible?: boolean | ((tab: TabOption, allTabs: TabOption[]) => boolean);
}

export interface TabsProps {
  // Accessibility & Semantic HTML
  as?: "nav" | "div";
  className?: string;
  defaultValue?: string | number;
  draggable?: boolean;
  onTabsReorder?: (reorderedTabs: TabOption[]) => void;
  onValueChange?: (value: string | number) => void;
  orientation?: "horizontal" | "vertical";
  separatorClassName?: string;
  showSeparators?: boolean;
  tabs: TabOption[];
  value?: string | number;

  // Layout & Styling
  variant?: "default" | "line";
}

// Internal DraggableTabOption component
interface DraggableTabOptionProps {
  isDraggable: boolean;
  tab: TabOption;
}

function DraggableTabOption({ tab, isDraggable }: DraggableTabOptionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(tab.id),
    disabled: !isDraggable || tab.disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDraggable && !tab.disabled ? "grab" : "default",
  };

  return (
    <TabsTrigger
      aria-label={tab.ariaLabel || tab.label}
      disabled={tab.disabled}
      ref={setNodeRef}
      style={style}
      value={String(tab.id)}
      {...(isDraggable && !tab.disabled ? attributes : {})}
      {...(isDraggable && !tab.disabled ? listeners : {})}
    >
      {renderTabTriggerContent(tab)}
    </TabsTrigger>
  );
}

export function Tabs({
  tabs,
  defaultValue,
  value: controlledValue,
  onValueChange,
  onTabsReorder,
  variant = "default",
  orientation = "horizontal",
  draggable = false,
  showSeparators = false,
  separatorClassName,
  as: Component = "div",
  className,
}: TabsProps) {
  // State for tab order (when draggable)
  const [orderedTabs, setOrderedTabs] = useState<TabOption[]>(tabs);
  const [internalValue, setInternalValue] = useState<string | undefined>(
    defaultValue !== undefined ? String(defaultValue) : undefined
  );

  // Sync orderedTabs when tabs prop changes
  useEffect(() => {
    setOrderedTabs(tabs);
  }, [tabs]);

  // Filter visible tabs
  const visibleTabs = useMemo(() => {
    return filterVisibleTabs(orderedTabs);
  }, [orderedTabs]);

  // Ensure active tab is visible
  const currentValue = controlledValue ?? internalValue;
  useEffect(() => {
    if (!currentValue) {
      return;
    }

    const isActiveTabVisible = visibleTabs.some(
      (tab) => String(tab.id) === String(currentValue)
    );

    if (!isActiveTabVisible && visibleTabs.length > 0) {
      const firstVisibleTab = String(visibleTabs[0]?.id);
      if (controlledValue === undefined) {
        setInternalValue(firstVisibleTab);
      }
      onValueChange?.(firstVisibleTab);
    }
  }, [currentValue, visibleTabs, controlledValue, onValueChange]);

  // Set default value to first visible tab if not provided
  let effectiveValue: string | undefined;
  if (currentValue !== undefined) {
    effectiveValue = String(currentValue);
  } else if (visibleTabs.length > 0) {
    effectiveValue = String(visibleTabs[0]?.id);
  } else {
    effectiveValue = undefined;
  }

  // Handle value change
  const handleValueChange = (newValue: string) => {
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedTabs((items) => {
        const oldIndex = items.findIndex(
          (item) => String(item.id) === String(active.id)
        );
        const newIndex = items.findIndex(
          (item) => String(item.id) === String(over.id)
        );
        const reordered = arrayMove(items, oldIndex, newIndex);
        onTabsReorder?.(reordered);
        return reordered;
      });
    }
  };

  // Empty state when no tabs are visible
  if (visibleTabs.length === 0) {
    return (
      <Component
        className={cn(
          "flex items-center justify-center py-4 text-muted-foreground",
          className
        )}
      >
        No tabs available
      </Component>
    );
  }

  // Sortable IDs for drag-and-drop
  const sortableIds = visibleTabs.map((tab) => String(tab.id));

  // Choose sorting strategy based on orientation
  const sortingStrategy =
    orientation === "vertical"
      ? verticalListSortingStrategy
      : horizontalListSortingStrategy;

  return (
    <Component className={cn("w-full", className)}>
      <ShadcnTabs
        onValueChange={handleValueChange}
        orientation={orientation}
        value={effectiveValue}
      >
        {draggable ? (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext items={sortableIds} strategy={sortingStrategy}>
              <TabsList variant={variant}>
                {visibleTabs.map((tab, index) => (
                  <Fragment key={tab.id}>
                    <DraggableTabOption isDraggable={draggable} tab={tab} />
                    {showSeparators && index < visibleTabs.length - 1 && (
                      <Separator
                        className={cn(
                          orientation === "vertical"
                            ? "my-1 w-full"
                            : "mx-1 h-6",
                          separatorClassName
                        )}
                        orientation={
                          orientation === "vertical" ? "horizontal" : "vertical"
                        }
                      />
                    )}
                  </Fragment>
                ))}
              </TabsList>
            </SortableContext>
          </DndContext>
        ) : (
          <TabsList variant={variant}>
            {visibleTabs.map((tab, index) => (
              <Fragment key={tab.id}>
                <DraggableTabOption isDraggable={false} tab={tab} />
                {showSeparators && index < visibleTabs.length - 1 && (
                  <Separator
                    className={cn(
                      orientation === "vertical" ? "my-1 w-full" : "mx-1 h-6",
                      separatorClassName
                    )}
                    orientation={
                      orientation === "vertical" ? "horizontal" : "vertical"
                    }
                  />
                )}
              </Fragment>
            ))}
          </TabsList>
        )}
      </ShadcnTabs>
    </Component>
  );
}
